import { useEffect } from "react";

interface ShortcutHandlers {
  onSearch?: () => void;
  onNew?: () => void;
  onToggleView?: () => void;
  onFilter?: () => void;
  onHelp?: () => void;
  onEscape?: () => void;
}

/**
 * Global, single-key shortcuts. Skips when the user is typing in an input
 * (text/textarea/select/[contenteditable]) or holding modifier keys —
 * those are reserved for browser/OS chords.
 *
 * Map:
 *   /  → search focus
 *   n  → new item
 *   g  → toggle grid/list view
 *   f  → open filter
 *   ?  → help overlay (must NOT fire while typing)
 *   Esc → caller-defined (always fires, even from inputs)
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers): void {
  useEffect(() => {
    function isTyping(target: EventTarget | null): boolean {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    }

    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape") {
        handlers.onEscape?.();
        return;
      }
      if (isTyping(e.target)) return;

      // The "?" key reports as e.key === "?" on US layouts (shift+/).
      if (e.key === "?" && handlers.onHelp) {
        e.preventDefault();
        handlers.onHelp();
        return;
      }

      switch (e.key) {
        case "/":
          if (handlers.onSearch) {
            e.preventDefault();
            handlers.onSearch();
          }
          break;
        case "n":
        case "N":
          if (handlers.onNew) {
            e.preventDefault();
            handlers.onNew();
          }
          break;
        case "g":
        case "G":
          if (handlers.onToggleView) {
            e.preventDefault();
            handlers.onToggleView();
          }
          break;
        case "f":
        case "F":
          if (handlers.onFilter) {
            e.preventDefault();
            handlers.onFilter();
          }
          break;
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handlers]);
}
